<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue'
import { UiButton, UiIconButton, UiInfoBox, UiText, UiTooltip, type UiIconProps } from '../ui'

export type AdminEditorPreviewViewport = 'fit' | 'mobile' | 'tablet' | 'desktop'

const props = defineProps<{
  url: string
  revision: number
  error?: string
  label?: string
  labelPath?: string
  isFullscreen?: boolean
  viewport: AdminEditorPreviewViewport
  viewportOptions: readonly { label: string; value: AdminEditorPreviewViewport }[]
  viewportWidth?: number | null
}>()

const emit = defineEmits<{
  (event: 'frame-load'): void
  (event: 'update:viewport', value: AdminEditorPreviewViewport): void
  (event: 'frame-ready', frame: HTMLIFrameElement | null): void
  (event: 'select-label'): void
  (event: 'toggle-fullscreen'): void
}>()

const frameSrc = computed(() => props.url || '/')
const frameElement = ref<HTMLIFrameElement | null>(null)
const viewportRef = ref<HTMLElement | null>(null)
const availableWidth = ref(0)
const availableHeight = ref(0)
let resizeObserver: ResizeObserver | null = null

const targetHeight = 900
const viewportIcons: Record<AdminEditorPreviewViewport, UiIconProps['name']> = {
  fit: 'maximize-01',
  mobile: 'phone-01',
  tablet: 'tablet-01',
  desktop: 'monitor-01',
}

const scale = computed(() => {
  if (!props.viewportWidth || availableWidth.value <= 0 || availableHeight.value <= 0) return 1
  const widthScale = (availableWidth.value - 24) / props.viewportWidth
  const heightScale = (availableHeight.value - 24) / targetHeight
  return Math.min(1, Math.max(0.2, Math.min(widthScale, heightScale)))
})

const scaledFrameStyle = computed<CSSProperties>(() => ({
  width: `${props.viewportWidth || 0}px`,
  height: `${targetHeight}px`,
  transform: `scale(${scale.value})`,
  transformOrigin: 'top left',
}))

const scaledShellStyle = computed<CSSProperties>(() => ({
  width: props.viewportWidth ? `${props.viewportWidth * scale.value}px` : '100%',
  height: props.viewportWidth ? `${targetHeight * scale.value}px` : '100%',
}))

onMounted(() => {
  if (!viewportRef.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver((entries) => {
    availableWidth.value = entries[0]?.contentRect.width ?? 0
    availableHeight.value = entries[0]?.contentRect.height ?? 0
  })
  resizeObserver.observe(viewportRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  emit('frame-ready', null)
})

watch(frameElement, (frame) => {
  emit('frame-ready', frame)
}, { flush: 'post', immediate: true })
</script>

<template>
  <div
    class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-se-gray-300 bg-white shadow-sm"
    :class="{ 'fixed left-0 top-0 z-[9999] h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-[100dvw] rounded-none border-0': props.isFullscreen }"
    :data-preview-fullscreen="props.isFullscreen ? 'true' : undefined"
  >
    <div class="flex min-h-12 items-center gap-3 border-b border-se-gray-300 bg-se-gray-50 px-3 py-2">
      <UiTooltip :content="props.isFullscreen ? 'Exit fullscreen' : 'Fullscreen preview'">
        <UiIconButton
          :icon="props.isFullscreen ? 'minimize-01' : 'expand-01'"
          :aria-label="props.isFullscreen ? 'Exit fullscreen' : 'Fullscreen preview'"
          variant="ghost"
          hover-style="highlight"
          class="h-7 w-7 shrink-0"
          @click="emit('toggle-fullscreen')"
        />
      </UiTooltip>

      <div class="min-w-0 flex-1 px-1 py-1.5">
        <UiText as="p" variant="caption-normal" :text="frameSrc" class="truncate text-se-gray-700" />
      </div>

      <UiTooltip
        v-if="props.label"
        :content="props.labelPath || props.label"
      >
        <UiButton
          icon="file-placeholder"
          :label="props.label"
          size="sm"
          variant="outline"
          class="max-w-56 shrink-0"
          @click="emit('select-label')"
        />
      </UiTooltip>

      <div class="flex shrink-0 items-center gap-1">
        <UiTooltip
          v-for="option in props.viewportOptions"
          :key="option.value"
          :content="option.label"
        >
          <UiIconButton
            :icon="viewportIcons[option.value]"
            :aria-label="option.label"
            variant="ghost"
            hover-style="highlight"
            :class="props.viewport === option.value ? 'h-7 w-7 bg-se-gray-200 text-se-dark' : 'h-7 w-7'"
            @click="emit('update:viewport', option.value)"
          />
        </UiTooltip>
      </div>
    </div>

    <div
      ref="viewportRef"
      class="flex min-h-0 flex-1 justify-center overflow-auto"
      :class="props.viewportWidth ? 'bg-se-gray-100 p-3' : 'bg-white p-0'"
    >
      <UiInfoBox v-if="props.error" variant="warning" class="m-3">
        <UiText as="p" variant="p-small" :text="props.error" />
      </UiInfoBox>

      <iframe
        v-if="!props.error && !props.viewportWidth"
        ref="frameElement"
        :key="`${frameSrc}:${props.revision}:fit`"
        :src="frameSrc"
        class="h-full min-h-0 w-full border-0 bg-white"
        title="Editor preview"
        @load="emit('frame-load')"
      />

      <div
        v-else-if="!props.error"
        class="overflow-hidden rounded-sm border border-se-gray-300 bg-white"
        :style="scaledShellStyle"
      >
        <iframe
          ref="frameElement"
          :key="`${frameSrc}:${props.revision}:${props.viewportWidth || 'fit'}`"
          :src="frameSrc"
          class="block border-0 bg-white"
          title="Editor preview"
          :style="scaledFrameStyle"
          @load="emit('frame-load')"
        />
      </div>
    </div>
  </div>
</template>
